import { Alert, Button, Col, Divider, Row, Spin, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import Card from '../../../components/Card/Card';
import './CampaignConcept.css';
import { useEffect, useState } from 'react';
import { fetchRequest } from '../../../utils/Fetcher';
import { useSelector } from 'react-redux';
import { selectSessionId } from '../../../store/sessionSlice';
import { SaveProgress } from '../../../utils/SaveProgress';

const CampaignConcept = ({ selectedPersonaId, onNext }) => {
    const desc = "Choose one or more campaign concept";

    const [campaignConcepts, setCampaignConcepts] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]); // keep array
    const [loading, setLoading] = useState(false);
    const [loadingDesc, setLoadingDesc] = useState("");
    const [error, setError] = useState("");

    const [promptById, setPromptById] = useState({});
    const [revisingId, setRevisingId] = useState(null);

    const sessionId = useSelector(selectSessionId);

    const onButtonClick = async () => {
        await SaveProgress({
            sessionId: sessionId,
            method: "POST",
            param: `campaign-concept/save`,
            payload: {
                selectedIds: selectedIds
            }
        });

        onNext(selectedIds);
    }

    const toggleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const hidePrompt = selectedIds.length > 0;

    const getCampaignConcept = async () => {
        setLoadingDesc("Fetching campaign concept...");
        const { result } = await fetchRequest({
            method: "GET",
            path: `sessions/campaign-concept`,
        });
        return result;
    };

    const postGenerateCampaignConcept = async () => {
        setLoadingDesc("AI is generating your campaign concept, please wait...");

        const body = {
            customerPersonaId: selectedPersonaId,
            numOfPrompts: 3
        }

        const { result } = await fetchRequest({
            method: "POST",
            path: `sessions/campaign-concept/generate`,
            data: JSON.stringify(body)
        });
        return result;
    };

    const handleReviseConcept = async (conceptId) => {
        const instruction = (promptById[conceptId] || "").trim();
        if (!instruction) return;

        try {
            setRevisingId(conceptId);

            const { result } = await fetchRequest({
                method: "PATCH",
                path: `sessions/campaign-concept/${conceptId}/revise`,
                data: JSON.stringify({ revision_instructions: instruction }),
            });

            const updated = result?.data ?? result;

            setCampaignConcepts((prev) =>
                prev.map((c) =>
                    c.id === conceptId
                    ? {
                        ...c,
                        coreMessage: updated.coreMessage,
                        title: updated.title,
                        tagline: updated.tagline,
                    }
                    : c
            ));

            setPromptById((prev) => ({ ...prev, [conceptId]: "" }));
        } finally {
            setRevisingId(null);
        }
    };

    const keepValidSelectedIds = (latestList, prevSelectedIds) => {
        const valid = new Set((latestList ?? []).map((x) => x.id));
        return (prevSelectedIds ?? []).filter((id) => valid.has(id));
    };

    useEffect(() => {
        if (!sessionId) return;

        let cancelled = false;

        const normalizeList = (payload) => {
            const list = payload?.data ?? payload?.items ?? payload;
            return Array.isArray(list) ? list : [];
        };

        const load = async () => {
            try {
            setLoading(true);
            setError("");

            const existing = normalizeList(await getCampaignConcept());
            if (cancelled) return;

            if (existing.length === 0) {
                await postGenerateCampaignConcept();
                if (cancelled) return;
            }

            const latest = normalizeList(await getCampaignConcept());
            if (cancelled) return;

            setCampaignConcepts(latest);

            setSelectedIds((prev) => keepValidSelectedIds(latest, prev));
            } catch (e) {
                if (!cancelled) setError(e.message || String(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [sessionId, selectedPersonaId]);

    const campConceptContent = (title, tagline, bio) => {
        return (
        <div>
            <h4 className="campaign-title">{title}</h4>
            <div className="campaign-tagline">{tagline}</div>
            <div>{bio}</div>
        </div>
        )
    }

    return (
        <div>
            <div className="content-title-container">
                <div className="content-title">
                    {desc}
                    <Tooltip placement="bottom" title={process.env.REACT_APP_TOOLTIP_CAMPAIGN_CONCEPT}>
                        <QuestionCircleOutlined style={{ marginLeft: 8, fontSize: 14, color: '#888', cursor: 'pointer' }} />
                    </Tooltip>
                </div>
                <Divider className="content-title-divider" />
            </div>

            {error && (
                <Alert
                type="error"
                showIcon
                title="Couldn’t load customer personas"
                description={error}
                style={{ margin: 20 }}
                />
            )}

            {loading ? (
                <div className='loading-container'>
                {loadingDesc}
                <Spin />
                </div>
            ) : (
                <Row gutter={[10, 10]} className="campaign-concept-container">
                    {campaignConcepts.map((item) => (
                        <Col xs={24} sm={12} md={8} key={item.id}>
                            <Card
                                content={campConceptContent(item.title, item.tagline, item.coreMessage)}
                                isSelected={selectedIds.includes(item.id)}
                                hidePrompt={hidePrompt}
                                onSelect={() => toggleSelect(item.id)}
                                promptValue={promptById[item.id] || ""}
                                onPromptChange={(val) => setPromptById((prev) => ({ ...prev, [item.id]: val }))}
                                onRevise={() => handleReviseConcept(item.id)}
                                revising={revisingId === item.id}
                            />

                        </Col>
                    ))}
                </Row>
            )}

            {selectedIds.length !== 0 && <div className="back-next-button-cc">
                <Button onClick={onButtonClick}>Next</Button>
            </div>}
        </div>
    );
}

export default CampaignConcept;